import { ReactNode, memo, useRef } from 'react';
import { useFormContext, UseFormRegister } from 'react-hook-form';
import toast from 'react-hot-toast';
import { FormInput } from '@components/FormInput/ui/FormInput';
import IconButton from '@components/IconButton/ui/IconButton';
import { Toast } from '@components/Toast/ui/Toast';
import CloseIcon from '@icons/CloseIcon/CloseIcon';
import { useMonoDemandFormContext } from '@providers/DemandMonoFormProvider/model/DemandMonoFormContext';
import { ALLOWED_FILE_EXTENSIONS, FILE_SIZE_LIMIT } from '@static/staticData';
import { SeparatedFormKeys } from '@features/DemandFormTemplates/model/DemandForm';
import { useDocumentPreview } from '@components/DocumentPreviewDialog/useDocumentPreview';
import styles from './TableRowCell.module.scss';

interface ITableRowCellProps {
  name: string;
  key?: string;
  width?: string;
  color?: string;
  fontFamily?: string;
  type?: 'input' | 'select';
  placeHolder?: string;
  image?: any;
  wrapperFn?: (values: any, id?: any) => ReactNode;
  sortMethod?: (data: any[], direction: string) => void;
  value: any;
  actions?: boolean;
  rowActions?: (classname: string, data?: any) => ReactNode;
  id?: string;
  data?: any;
  register?: UseFormRegister<any>;
  rowIndex?: number;
  keyName?: string;
  values?: string[];
  className?: string;
  columnType?: string;
}

const throwError = (errorMessage: string) => {
  toast(({ id }) => (
    <Toast id={id} type="error" title="Error" text={errorMessage} />
  ));
};

const validateFiles = (filesToValidate: File[]): File[] => {
  return filesToValidate.filter((file) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (file.size > FILE_SIZE_LIMIT) {
      throwError(`${file.name} file too big`);
      return false;
    }

    if (!fileExtension) {
      throwError('Incorrect file type');
      return false;
    }

    if (!ALLOWED_FILE_EXTENSIONS.includes(fileExtension)) {
      throwError(
        `Only ${ALLOWED_FILE_EXTENSIONS.join(', ')} files are allowed.`
      );
      return false;
    }

    return true;
  });
};

/**
 * @param props - The props for the component.
 * @param name - The table row name.
 * @param key - The table row key.
 * @param width - The table row custom width.
 * @param color - The table row custom color.
 * @param fontFamily - The table row custom font family.
 * @param type - The table row type 'input' | 'select'
 * @param placeHolder - The table row custom placeholder
 * @param image - An image or icon representing the table row.
 * @param rowActions - Actions which is called if the user click to the row
 * @param roundLastRow - Indicates whether last row should be rounded
 * @param rowHeight - The row height
 * @param register - the path name to the form field value, name is required and unique
 * @returns A React component that displays the tab row.
 */

function TableRowCellComponent(props: ITableRowCellProps) {
  const {
    wrapperFn,
    value,
    image,
    color,
    fontFamily,
    width,
    actions,
    rowActions,
    id,
    data,
    placeHolder,
    type,
    register,
    keyName,
    values = [],
    className,
    columnType,
  } = props;
  let element: ReactNode = null;
  const { setCustomDocumentParsers } = useMonoDemandFormContext();
  const fileRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileContainerRef = useRef<HTMLDivElement | null>(null);
  const formContext = useFormContext();
  const { openPreview, previewDialog } = useDocumentPreview();
  if (placeHolder) {
    element = (
      <FormInput
        bordered={false}
        placeholder={placeHolder}
        type={type}
        values={values}
        {...register!(`${data.tableKey}.${data.rowIndex}.${keyName}` as const)}
      />
    );
  } else if (wrapperFn) {
    element = wrapperFn(value, id);
  } else {
    element = (
      <>
        {image}
        <p style={{ color, fontFamily }}>{value}</p>
      </>
    );
  }

  const handleDeleteFile = async (fileName: string) => {
    setCustomDocumentParsers?.((prevState) => {
      if (!Array.isArray(prevState)) {
        return prevState;
      }

      const nextState = [...prevState];
      const currentRow = nextState[data.rowIndex];
      if (!currentRow) {
        return prevState;
      }

      const existingFiles = currentRow.files ?? [];

      if (existingFiles.length <= 1) {
        toast(({ id: toastId }) => (
          <Toast
            id={toastId}
            type="error"
            title="Error"
            text="Parser should have at least one file"
          />
        ));
        return prevState;
      }

      nextState[data.rowIndex] = {
        ...currentRow,
        files: existingFiles.filter((file: any) => file.name !== fileName),
      };

      return nextState;
    });
    if (formContext) {
      await formContext.trigger(SeparatedFormKeys[2]);
    }
  };

  const handleAddFiles = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // ZLAYT-1445: a parser file opens in the in-app preview, not a new tab on
  // the storage URL (QA D-21). A file just added locally has no URL yet.
  const previewParserFile = (url: string | undefined, name: string) => () => {
    openPreview(url, name);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const incomingFiles = Array.from(event.target.files || []);

    const validatedFiles = validateFiles(incomingFiles);

    setCustomDocumentParsers?.((prevState) => {
      if (!Array.isArray(prevState) || !prevState[data.rowIndex]) {
        return prevState;
      }

      const nextState = [...prevState];
      const currentRow = nextState[data.rowIndex];
      if (!currentRow) {
        return prevState;
      }

      const existingFiles = currentRow.files ?? [];
      const uniqueNewFiles = validatedFiles.filter(
        (candidate) =>
          !existingFiles.some(
            (existingFile) => existingFile.name === candidate.name
          )
      );

      nextState[data.rowIndex] = {
        ...currentRow,
        files: existingFiles.concat(uniqueNewFiles),
      };

      return nextState;
    });

    if (formContext) {
      await formContext.trigger(SeparatedFormKeys[2]);
    }
  };

  if (columnType === 'create-demand-files') {
    element = (
      <div
        key={data.rowIndex}
        ref={fileContainerRef}
        className={styles.filesContainer}
      >
        {value.map((file: any) => (
          <div
            key={file.name}
            className={styles.fileName}
            ref={(el) => {
              fileRefs.current[file.name] = el;
            }}
          >
            <button
              type="button"
              onClick={previewParserFile(file.url, file.name)}
            >
              {file.name}
            </button>
            <div className={styles.iconBox}>
              {data.is_edit && (
                <IconButton
                  className={styles.deleteFileBtn}
                  onClick={() => {
                    handleDeleteFile(file.name);
                  }}
                >
                  <CloseIcon stroke="#667085" />
                </IconButton>
              )}
            </div>
          </div>
        ))}
        {data.is_edit && (
          <button type="button" className={styles.add} onClick={handleAddFiles}>
            <span /> Add files
          </button>
        )}
        <input
          type="file"
          accept=".pdf, .doc, .docx"
          multiple
          onChange={handleFileChange}
          style={{ display: 'none' }}
          ref={fileInputRef}
        />
        {previewDialog}
      </div>
    );
  }

  if (columnType === 'sections') {
    element = (
      <div
        className={[styles.tableRowCell, styles.sections, className].join(' ')}
      >
        {value.map((section: any, index: number) => {
          if (index === value.length - 1) {
            return section.label;
          }
          return `${section.label}, `;
        })}
      </div>
    );
  }

  return (
    <div
      className={[styles.tableRowCell, className].join(' ')}
      style={{ minWidth: width ?? 'unset', maxWidth: width ?? 'unset' }}
    >
      {actions && rowActions
        ? rowActions(styles.rowActions, { id, ...data })
        : element}
    </div>
  );
}

const TableRowCell = memo(TableRowCellComponent);

export default TableRowCell;
